import { isEmpty } from "lodash";
import convertUnit from "utils/convertUnit";

import { getUpgradeMatchForType } from "utils/upgrades/findUpgradeMatch";

function pick(obj, path, fallback = "—") {
    if (path == null || typeof path !== "string") {
        console.warn("[HPXML audit] voltaSystemsAudit.pick: invalid path", path);
        return fallback;
    }
    const v = path.split(".").reduce((o, k) => (o && o[k] != null ? o[k] : undefined), obj);
    if (v == null || v === "") return fallback;
    if (typeof v === "object" && !Array.isArray(v)) {
        const id = v.id;
        const val = v.value;
        if (val != null && val !== "") return String(val);
        if (id != null && id !== "") return String(id);
        return fallback;
    }
    return String(v);
}

function linesFromObject(prefix, obj, keys) {
    if (isEmpty(obj)) return [];
    return keys.map(([k, label]) => ({
        key: `${prefix}: ${label}`,
        value: pick(obj, k),
    }));
}

/**
 * Compact Volta SNAP summaries for side-by-side HPXML audit (first-pass fields).
 */
export function buildVoltaSystemsAudit(modelData = {}, upgrades = {}, upgradePackageId = "") {
    try {
        const { fields: ventilationUpgradeFields } = getUpgradeMatchForType(upgrades, "ventilation", upgradePackageId);
        const { fields: heatingCoolingUpgradeFields } = getUpgradeMatchForType(
            upgrades,
            "heatingCooling",
            upgradePackageId
        );
        const { fields: domesticHotWaterUpgradeFields } = getUpgradeMatchForType(
            upgrades,
            "domesticHotWater",
            upgradePackageId
        );
        const { fields: generationUpgradeFields } = getUpgradeMatchForType(upgrades, "generation", upgradePackageId);

        const ventilation = ventilationUpgradeFields || modelData.ventilation || {};
        const heatingCooling = heatingCoolingUpgradeFields || modelData.heatingCooling || {};
        const domesticHotWater = domesticHotWaterUpgradeFields || modelData.domesticHotWater || {};
        const generation = generationUpgradeFields || modelData.generation || {};

        const wholeHrv = ventilation.wholeHouseSystems?.hrv || {};
        const wholeVent = ventilation.wholeHouseSystems?.ventilators || {};
        const supplementalVent = ventilation.supplementalSystems?.ventilators || {};

        const heating = heatingCooling.heating?.system || {};
        const cooling = heatingCooling.cooling?.system || {};
        const suppl = heatingCooling.supplSystems || {};
        const radiant = heatingCooling.radiantHeating || {};

        const primaryDhw = domesticHotWater.primarySystem || {};
        const dwhr = primaryDhw.drainWaterHeatRecovery || {};

        const pv = generation || {};

        const coolingHpSystemType = Object.keys(cooling)[0];

        const {
            specifications: {
                coolingEfficiency: { isCop: isCoolingCop = true } = {},
                heatingEfficiency: { isCop: isHeatingCop = true } = {},
            } = {},
        } = cooling[coolingHpSystemType] || {};

        return {
            ventilation: [
                {
                    key: "HRV Supply / Exhaust (cfm)",
                    value: convertUnit({
                        value: wholeHrv.supplyFlowrate,
                        type: "airFlow",
                        inputUnit: "L/s",
                        outputUnit: "cfm",
                    }).toFixed(1),
                },

                { key: "HRV Fan Power (W)", value: wholeHrv.fanPower1 },
                { key: "HRV Efficiency (%)", value: wholeHrv.efficiency1 },
                ...Object.values(wholeVent).reduce((acc, vent, index) => {
                    acc.push({
                        key: `Ventilator ${index + 1} supply / exhaust (L/s)`,
                        value: `${convertUnit({
                            value: vent.supplyFlowrate,
                            type: "airFlow",
                            inputUnit: "L/s",
                            outputUnit: "cfm",
                        }).toFixed(1)} / ${convertUnit({
                            value: vent.exhaustFlowrate,
                            type: "airFlow",
                            inputUnit: "L/s",
                            outputUnit: "cfm",
                        }).toFixed(1)}`,
                    });
                    return acc;
                }, []),
                ...Object.values(supplementalVent).reduce((acc, vent, index) => {
                    acc.push({
                        key: `Supplemental Ventilator ${index + 1} supply / exhaust (L/s)`,
                        value: `${convertUnit({
                            value: vent.supplyFlowrate,
                            type: "airFlow",
                            inputUnit: "L/s",
                            outputUnit: "cfm",
                        }).toFixed(1)} / ${convertUnit({
                            value: vent.exhaustFlowrate,
                            type: "airFlow",
                            inputUnit: "L/s",
                            outputUnit: "cfm",
                        }).toFixed(1)}`,
                    });
                    return acc;
                }, []),
            ],
            spaceHeating: [
                ...linesFromObject("Furnace", heating, [
                    ["furnace.specifications.efficiency", "Efficiency %"],
                    ["furnace.specifications.outputCapacity.value", "Output capacity (kW)"],
                ]),
                ...linesFromObject("Baseboards", heating.baseboards || {}, [
                    ["manufacturer", "Manufacturer"],
                    ["model", "Model"],
                    ["specifications.efficiency", "Efficiency %"],
                    ["specifications.outputCapacity.value", "Output capacity (kW)"],
                ]),
                ...linesFromObject("Boiler", heating.boiler || {}, [
                    ["boiler.specifications.efficiency", "Efficiency %"],
                    ["boiler.specifications.outputCapacity.value", "Output capacity (kW)"],
                ]),
                ...linesFromObject("Combo", heating.combo || {}, [
                    ["specifications.efficiency", "Efficiency %"],
                    ["specifications.outputCapacity.value", "Output capacity (kW)"],
                ]),
                ...linesFromObject("P9 Combo", heating.p9Combo || {}, [
                    ["thermalPerformanceFactor", "TPF"],
                    ["spaceHeatingCapacity", "Output capacity (W)"],
                ]),
            ],
            secondaryHeating: linesFromObject("Supplemental", suppl, [
                ["manufacturer", "Manufacturer"],
                ["model", "Model"],
            ]),
            coolingAndHeatPumps: [
                ...linesFromObject("Cooling", cooling.airConditioning || {}, [
                    ["specifications.coolingEfficiency.value", `Cooling Efficiency (${isCoolingCop ? "COP" : "SEER"})`],
                    ["specifications.outputCapacity.value", "Capacity (kW)"],
                ]),
                ...linesFromObject("ASHP", cooling.airHeatPump || {}, [
                    ["specifications.coolingEfficiency", `Cooling efficiency (${isCoolingCop ? "COP" : "SEER"})`],
                    ["specifications.heatingEfficiency", `Heating efficiency (${isHeatingCop ? "COP" : "HSPF"})`],
                    ["specifications.outputCapacity.value", "Capacity (kW)"],
                ]),
                ...linesFromObject("GSHP", cooling.groundHeatPump || {}, [
                    ["specifications.coolingEfficiency", `Cooling efficiency (${isCoolingCop ? "COP" : "SEER"})`],
                    ["specifications.heatingEfficiency", `Heating efficiency (${isHeatingCop ? "COP" : "HSPF"})`],
                    ["specifications.outputCapacity.value", "Capacity (kW)"],
                ]),
                ...linesFromObject("WSHP", cooling.waterHeatPump || {}, [
                    ["specifications.coolingEfficiency", `Cooling efficiency (${isCoolingCop ? "COP" : "SEER"})`],
                    ["specifications.heatingEfficiency", `Heating efficiency (${isHeatingCop ? "COP" : "HSPF"})`],
                    ["specifications.outputCapacity.value", "Capacity (kW)"],
                ]),
            ],
            domesticHotWater: linesFromObject("Primary DHW", primaryDhw, [
                ["tankVolume.value", "Tank volume (L)"],
                ["energyFactor.value", "Energy factor"],
                ["heatPumpCOP", "HP COP"],
            ]),
            drainWaterHeatRecovery: linesFromObject("DWHR", dwhr, [["efficiencyRating", "Efficiency (%)"]]),
            photovoltaics: [
                ...linesFromObject("PV", pv, [
                    ["pvCapacity", "PV capacity (kW)"],
                    ["solarReady", "Solar ready"],
                ]),
                ...(isEmpty(pv.pvSystems)
                    ? []
                    : [{ key: "PV: systems", value: `${Object.keys(pv.pvSystems).length} system(s)` }]),
            ],
        };
    } catch (err) {
        const wrapped = new Error(`Volta systems audit: ${err instanceof Error ? err.message : String(err)}`);
        wrapped.cause = err instanceof Error ? err : undefined;
        wrapped.auditContext = { phase: "voltaSystems" };
        throw wrapped;
    }
}
